// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAdvisor } from '../contexts/AdvisorContext';

export type ViewMode = 'my-data' | 'all-advisors';

export interface UseAdminViewToggleOptions {
  /** Storage key for sessionStorage (unique per page) */
  storageKey: string;
  /** Whether to sync with URL query parameter */
  syncWithUrl?: boolean;
}

export interface UseAdminViewToggleReturn {
  /** Current view mode */
  currentView: ViewMode;
  /** Function to update view mode */
  setCurrentView: (view: ViewMode) => void;
  /** Whether current view is the admin view */
  isAdminView: boolean;
  /** Whether to show the toggle (true only for admin users) */
  showToggle: boolean;
  /** Whether the hook is still initializing */
  isInitializing: boolean;
}

const SESSION_STORAGE_PREFIX = 'advisor-ui.viewToggle.';
const URL_PARAM_KEY = 'view';

/**
 * Custom hook for managing admin view toggle state
 *
 * Features:
 * - Persists view preference in sessionStorage (session-scoped)
 * - Syncs with URL query parameter for deep linking
 * - URL parameter takes precedence over sessionStorage (for redirects)
 * - Only shows toggle for admin users
 * - Defaults to "my-data" for new sessions
 *
 * @param options Configuration options
 * @returns View state and control functions
 */
export function useAdminViewToggle(
  options: UseAdminViewToggleOptions
): UseAdminViewToggleReturn {
  const { storageKey, syncWithUrl = true } = options;
  const { advisor, loading: advisorLoading } = useAdvisor();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isInitializing, setIsInitializing] = useState(true);

  // Check if user is admin
  const isAdmin = advisor?.role === 'ADMIN';

  // Full storage key with prefix
  const fullStorageKey = `${SESSION_STORAGE_PREFIX}${storageKey}`;

  // Initialize view state. The raw setter is renamed to `setCurrentViewState`
  // because `setCurrentView` (declared further down) is the public, wrapped
  // setter that also persists to sessionStorage and URL. Sonar S6754 about
  // the rename is intentional — see also useMaxFundingScenario.
  const [currentView, setCurrentViewState] = useState<ViewMode>('my-data');

  // Initialize from URL or sessionStorage on mount
  useEffect(() => {
    if (advisorLoading) return;

    // If not admin, always use 'my-data' and skip initialization
    if (!isAdmin) {
      setCurrentViewState('my-data');
      setIsInitializing(false);
      return;
    }

    // Priority 1: URL query parameter (for redirects and deep links)
    const urlView = searchParams.get(URL_PARAM_KEY);
    if (urlView === 'all' || urlView === 'all-advisors') {
      setCurrentViewState('all-advisors');
      // Also update sessionStorage
      try {
        sessionStorage.setItem(fullStorageKey, 'all-advisors');
      } catch (e) {
        console.warn('Failed to write to sessionStorage:', e);
      }
      setIsInitializing(false);
      return;
    }
    if (urlView === 'my-data' || urlView === 'personal') {
      setCurrentViewState('my-data');
      try {
        sessionStorage.setItem(fullStorageKey, 'my-data');
      } catch (e) {
        console.warn('Failed to write to sessionStorage:', e);
      }
      setIsInitializing(false);
      return;
    }

    // Priority 2: sessionStorage (for session persistence)
    try {
      const storedView = sessionStorage.getItem(fullStorageKey);
      if (storedView === 'my-data' || storedView === 'all-advisors') {
        setCurrentViewState(storedView);
        setIsInitializing(false);
        return;
      }
    } catch (e) {
      console.warn('Failed to read from sessionStorage:', e);
    }

    // Priority 3: Default to 'my-data'
    setCurrentViewState('my-data');
    setIsInitializing(false);
  }, [advisorLoading, isAdmin, searchParams, fullStorageKey]);

  // Update view state with side effects
  const setCurrentView = useCallback((view: ViewMode) => {
    // Non-admins cannot change view
    if (!isAdmin) return;

    setCurrentViewState(view);

    // Update sessionStorage
    try {
      sessionStorage.setItem(fullStorageKey, view);
    } catch (e) {
      console.warn('Failed to write to sessionStorage:', e);
    }

    // Update URL if syncWithUrl is enabled
    if (syncWithUrl) {
      const newParams = new URLSearchParams(searchParams);
      if (view === 'all-advisors') {
        newParams.set(URL_PARAM_KEY, 'all');
      } else {
        newParams.delete(URL_PARAM_KEY);
      }
      setSearchParams(newParams, { replace: true });
    }
  }, [isAdmin, fullStorageKey, syncWithUrl, searchParams, setSearchParams]);

  // Computed values
  const isAdminView = currentView === 'all-advisors';
  const showToggle = isAdmin;

  return {
    currentView,
    setCurrentView,
    isAdminView,
    showToggle,
    isInitializing: isInitializing || advisorLoading,
  };
}

export default useAdminViewToggle;
